import Vue from "vue";

const emptyData = {
    setting: null,
    logo_on_photo: null,
    price_list: null,
}

const state = {
    galleryId: null,
    picaServiceId: null,
    galleries: [],
    gallery: null,
    tags: null,
    data: {...emptyData},
    loading: {}
}

export const getters = {
    picaServiceId: state => state.picaServiceId,
    galleryId: state => state.galleryId,
    gallery: state => state.gallery,
    galleries: state => state.galleries,
    tags: state => state.tags,
    data: state => state.data,
    step_setting: state => state.data.setting,
    step_logo_on_photo: state => state.data.logo_on_photo,
    step_price_list: state => state.data.price_list,
}

export const mutations = {
    picaServiceId(state, id) {
        if (state.picaServiceId === id) return
        state.picaServiceId = id
        state.tags = null
        state.data = {...emptyData}
    },
    galleryId(state, id) {
        if (state.galleryId === id) return
        state.galleryId = id
        state.gallery = null
        state.tags = null
    },
    galleries(state, galleries) {
        state.galleries = galleries
    },
    gallery(state, gallery) {
        state.gallery = gallery
        state.data = {...emptyData}
    },
    deleteGallery(state, gallery) {
        // Helper function to recursively find and remove a gallery
        const removeGallery = (galleries, galleryToRemove) => {
            return galleries.map(g => {
                if (g.id === galleryToRemove.id) {
                    return null
                }
                if (g.sub && g.sub.length) {
                    const filteredSub = removeGallery(g.sub, galleryToRemove)
                    if (filteredSub.length !== g.sub.length) {
                        return { ...g, sub: filteredSub }
                    }
                }
                return g
            }).filter(g => g !== null)
        }

        state.galleries = removeGallery(state.galleries, gallery)
    },
    addGallery(state, gallery) {
        gallery.sub = []
        if (!gallery.parent_gallery) {
            state.galleries = [...state.galleries, gallery]
            return
        }

        // Helper function to recursively find the parent gallery and add the new gallery
        const addToParent = (galleries, newGallery) => {
            return galleries.map(g => {
                if (g.id === newGallery.parent_gallery) {
                    if (!g.sub) g.sub = []
                    g.sub.push(newGallery)
                    return g
                }
                if (g.sub && g.sub.length) {
                    return { ...g, sub: addToParent(g.sub, newGallery) }
                }
                return g
            })
        }

        state.galleries = addToParent(state.galleries, gallery)
    },
    sortGalleries(state, ids) {
        state.galleries = ids.map(id => state.galleries.find(g => g.id === id))
    },
    sortSubGalleries(state, {galleryId, ids}) {
        // Helper function to recursively find the gallery and sort its sub-galleries
        const sortGallerySubs = (galleries, targetId, sortedIds) => {
            return galleries.map(gallery => {
                if (gallery.id === targetId) {
                    return {
                        ...gallery,
                        sub: sortedIds.map(id => gallery.sub.find(g => g.id === id))
                    }
                }
                if (gallery.sub && gallery.sub.length) {
                    return {
                        ...gallery,
                        sub: sortGallerySubs(gallery.sub, targetId, sortedIds)
                    }
                }
                return gallery
            })
        }

        state.galleries = sortGallerySubs(state.galleries, galleryId, ids)
    },
    tags(state, tags) {
        state.tags = tags
    },
    data(state, {tab, data}) {
        Vue.set(state.data, tab, data)
    },
}

export const actions = service => ({
    setPicaServiceId({commit, state}, id) {
        if (state.picaServiceId === id) return false
        commit('picaServiceId', id)
        return true
    },
    setGalleryId({commit, state}, id) {
        if (state.galleryId === id) return false
        commit('galleryId', id)
        return true
    },
    async fetchGallery({commit, getters}, id) {
        const gallery = await service.fetchGallery(getters.picaServiceId, id)
        const perm = gallery.permissions
        commit('gallery', gallery)

        const config = Object.entries(perm).reduce((acc, [key, value]) => {
            acc[`gallery.${key}`] = value
            return acc
        }, {})

        commit('login/config', {
            config: config,
            type: 'gallery',
            id: getters.picaServiceId,
            refreshable: false
        }, {root: true})
        return true
    },
    async deleteGallery({commit, getters}, gallery) {
        await service.deleteGallery(getters.picaServiceId, gallery.id)
        commit('deleteGallery', gallery)
    },
    async createGallery({getters}, data) {
        await service.createGallery(getters.picaServiceId, data)
    },
    async updateSorting({commit, getters}, ids) {
        await service.updateSorting(getters.picaServiceId, ids)
        commit('sortGalleries', ids)
    },
    async updateSubSorting({commit, getters}, {galleryId, ids}) {
        await service.updateSorting(getters.picaServiceId, ids)
        commit('sortSubGalleries', {galleryId, ids})
    },
    async fetchTags({commit, getters}) {
        if (getters['tags']) return
        commit('tags', await service.fetchTags(getters.picaServiceId, getters.gallery.id))
    },
    async fetchTabData({commit, getters}, tab) {
        if (tab === 'browse_photo') return null
        if (tab === 'utility') return null
        let tabData = getters.data[tab]
        if (tabData) return tabData
        tabData = await service.fetchData(getters.picaServiceId, getters.gallery.id, tab)
        commit('data', {tab, data: tabData})
    },
    async updateTabData({commit, getters, dispatch}, {tab, data}) {
        const result = await service.updateData(getters.picaServiceId, getters.gallery.id, tab, data)
        if (tab === 'setting')
            await dispatch('fetchGallery', getters.gallery.id)
        commit('data', {
            tab,
            data: result
        })
    },
    async searchPriceList({getters}, params) {
        return await service.searchPriceList(getters.picaServiceId, params)
    },
})

export default service => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(service)
})
